/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// Added Permission

import { guestUsersApiRef } from '@agilelab/plugin-wb-auth';
import {
  ConfirmDialog,
  customAlertApiRef,
  usePermissions,
} from '@agilelab/plugin-wb-platform';
import { CustomError } from '@agilelab/plugin-wb-platform-common';
import { rbacRoleEditPermission } from '@agilelab/plugin-wb-rbac-common';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { CircularProgress, Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Popover from '@material-ui/core/Popover';
import MoreVert from '@material-ui/icons/MoreVert';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import { useQuery } from '@tanstack/react-query';
import { SyntheticEvent, useState } from 'react';

export function RolesPermissionsPageContextMenu() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [openDialog, setOpenDialog] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const guestUsersAuthApi = useApi(guestUsersApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const { allAllowed: allowedGuestUserDisable } = usePermissions([
    {
      permission: rbacRoleEditPermission,
    },
  ]);

  const {
    data: guestUserProviderInfo,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ['isGuestUserAuthEnabled', guestUsersAuthApi],
    queryFn: async () => {
      try {
        const guestUserInfo = await guestUsersAuthApi.getProviderInfo();
        return guestUserInfo;
      } catch (error) {
        return undefined;
      }
    },
    refetchOnWindowFocus: false,
  });

  const onOpen = (event: SyntheticEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(undefined);
  };

  const disableGuestUsers = async () => {
    try {
      setIsDisabling(true);
      const credentials = await identityApi.getCredentials();
      await guestUsersAuthApi.disableAll({ options: credentials });
      alertApi.post({
        message: 'Authenticated Guest Users disabled successfully',
        severity: 'success',
      });
      refetch();
      setIsDisabling(false);
    } catch (error) {
      alertApi.post({
        error: new CustomError(
          'Failed to disable Authenticated Guest Users',
          error.message,
        ),
        severity: 'error',
      });
    } finally {
      setOpenDialog(false);
    }
  };

  const enableGuestUsers = async () => {
    try {
      const credentials = await identityApi.getCredentials();
      await guestUsersAuthApi.enableAll({ options: credentials });
      alertApi.post({
        message: 'Authenticated Guest Users enabled successfully',
        severity: 'success',
      });
      refetch();
    } catch (error) {
      alertApi.post({
        error: new CustomError(
          'Failed to enable Authenticated Guest Users',
          error.message,
        ),
        severity: 'error',
      });
    }
  };

  if (loading) {
    return <CircularProgress size={18} color="primary" />;
  } else if (allowedGuestUserDisable && guestUserProviderInfo?.configured) {
    return (
      <>
        <IconButton
          aria-label="more"
          aria-controls="long-menu"
          aria-haspopup="true"
          onClick={onOpen}
          data-testid="menu-button"
          color="inherit"
          size="small"
        >
          <MoreVert />
        </IconButton>
        <Popover
          open={Boolean(anchorEl)}
          onClose={onClose}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuList>
            {guestUserProviderInfo.enabled ? (
              <MenuItem onClick={() => setOpenDialog(true)}>
                <ListItemIcon>
                  <RemoveCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Disable Authenticated Guest Users" />
              </MenuItem>
            ) : (
              <MenuItem onClick={enableGuestUsers}>
                <ListItemIcon>
                  <SupervisedUserCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Enable Authenticated Guest Users" />
              </MenuItem>
            )}
          </MenuList>
        </Popover>
        <ConfirmDialog
          title="Disabling Authenticated Guest Users"
          description={
            <>
              <Typography>
                By disabling Authenticated Guest Users you will need to login
                with another authentication method. All users currently logged
                in as Authenticated Guest Users will lose access to the
                platform.
              </Typography>
              <Typography style={{ fontWeight: '700' }}>
                Are you sure you want to disable Authenticated Guest Users?
              </Typography>
            </>
          }
          open={openDialog}
          onConfirm={disableGuestUsers}
          onClose={() => setOpenDialog(false)}
          inProgress={isDisabling}
          confirmButtonText="Yes, disable"
        />
      </>
    );
  }
  return undefined;
}
